import { Injectable, Injector } from "@angular/core";
import { Router } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { RouteType } from "src/app/models/enum";
import { User, UserProfile } from "src/app/models/models";
import { environment } from "src/environments/environment";
import { loginPathWithSessionUpgrade, SESSION } from "src/session.config";
import { ContainerRatingsService } from "../cbta-web/ratings/container-ratings-form/services/base/container-ratings.services";
import { HttpService } from "../shared/services/http.service";
import { LoaderService } from "../shared/services/loader.service";
import { MasterDataService } from "../shared/services/masterdata.service";
import { ProfileService } from "../shared/services/profile.service";
import { RoleService } from "../shared/services/role.service";
import {
	broadcastSessionRevoked,
	SESSION_REVOKED_AT_KEY,
} from "../shared/utils/auth-session-policy.utils";
import { assignWindowLocation } from "../shared/utils/browser-navigation.utils";
import { OnlineIdleLogoutService } from "./online-idle-logout.service";
import { consumeReturnUrl } from "./return-url.utils";

const LEGACY_OAUTH_KEYS = [
	"id_token",
	"access_token",
	"expires_at",
	"id_token_expires_at",
	"nonce",
	"session_state",
	"granted_scopes",
];

/** Aligns with backend 7-day sliding session for long-haul offline flights. */
const OFFLINE_SESSION_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

@Injectable({ providedIn: "root" })
export class GoogleAuthService {
	loginError?: string;
	authReLogin = false;
	private readonly offlineSessionDurationMs = OFFLINE_SESSION_DURATION_MS;
	private sessionLoaded = false;
	private sessionAuthenticated = false;
	private sessionResolvePromise?: Promise<boolean>;

	constructor(
		private router: Router,
		private httpService: HttpService,
		private loadingService: LoaderService,
		private roleService: RoleService,
		private initService: MasterDataService,
		private injector: Injector,
	) {
		window.addEventListener("storage", (e) => this.onStorageEvent(e));
	}

	async configure(): Promise<boolean> {
		this.purgeLegacyImplicitTokens();

		if (!navigator.onLine) {
			return this.hasValidOfflineSession();
		}

		try {
			const resp = await firstValueFrom(this.httpService.getMe());
			await this.hydrateUserSession(resp.data as User);
			this.sessionLoaded = true;
			this.sessionAuthenticated = true;
			this.startIdleWatcher();
			return true;
		} catch {
			this.sessionLoaded = true;
			this.sessionAuthenticated = false;
			this.stopIdleWatcher();
			return false;
		}
	}

	hasError(): boolean {
		return !!this.loginError;
	}

	onStorageEvent(event: StorageEvent): void {
		if (event.key !== SESSION_REVOKED_AT_KEY || event.newValue == null) return;
		this.stopIdleWatcher();
		this.clearLocalSession();
		assignWindowLocation(loginPathWithSessionUpgrade(RouteType.LOGIN));
	}

	authEvents(): void {
		// No-op: OAuth events removed in BFF mode.
	}

	/**
	 * Always probes GET /auth/me — ignores cached sessionAuthenticated.
	 * Use before protected data fetches (e.g. pending staging pull) after idle navigation.
	 */
	async revalidateSession(): Promise<boolean> {
		if (!navigator.onLine) {
			return this.hasValidOfflineSession();
		}

		try {
			await firstValueFrom(this.httpService.getMe());
			this.extendOfflineSessionWindow();
			this.sessionLoaded = true;
			this.sessionAuthenticated = true;
			this.startIdleWatcher();
			return true;
		} catch {
			this.sessionLoaded = true;
			this.sessionAuthenticated = false;
			this.stopIdleWatcher();
			return false;
		}
	}

	/**
	 * Re-validates the BFF session after reconnect via GET /auth/me.
	 * On success, hydrates the user and extends the offline grace window.
	 * Returns false when offline or when the server session is dead so callers
	 * can skip protected API work (e.g. draft sync) that would otherwise 401.
	 */
	async ensureValidTokenOnOnline(): Promise<boolean> {
		if (!navigator.onLine) {
			return false;
		}
		try {
			return await this.configure();
		} catch (err) {
			console.warn("Failed to refresh BFF session after coming online:", err);
			return false;
		}
	}

	signInToGoogle(): void {
		if (!navigator.onLine) {
			this.loginError = "You appear to be offline. Please reconnect and try again.";
			return;
		}

		this.loginError = undefined;
		const { origin, pathname, search } = window.location;
		const returnUrl = encodeURIComponent(`${origin}${pathname}${search}`);
		window.location.href = `${environment.apiDomain}/auth/login?returnUrl=${returnUrl}`;
	}

	/**
	 * Resolves BFF session before route guards.
	 * Online: only a live server session counts (no offline-grace fallback).
	 */
	async ensureSessionResolved(): Promise<boolean> {
		if (!navigator.onLine) {
			return this.hasValidOfflineSession();
		}
		if (this.sessionAuthenticated) return true;
		if (this.sessionLoaded) return false;
		if (!this.sessionResolvePromise) {
			this.sessionResolvePromise = this.configure().finally(() => {
				this.sessionResolvePromise = undefined;
			});
		}
		return this.sessionResolvePromise;
	}

	isAuthenticated(): boolean {
		return navigator.onLine ? this.sessionAuthenticated : this.hasValidOfflineSession();
	}

	/** True when cached profile exists and offlineSessionExpiresAt has not passed. */
	hasValidOfflineSession(): boolean {
		if (!this.hasOfflineProfile()) {
			return false;
		}
		const expiresAt = parseInt(
			localStorage.getItem(SESSION.SESSION_KEY_OFFLINE_SESSION_EXPIRES_AT) || "0",
			10,
		);
		return expiresAt > 0 && Date.now() < expiresAt;
	}

	isLoggedIn(): boolean {
		return this.isAuthenticated();
	}

	get userProfile(): UserProfile | null {
		const raw = localStorage.getItem(SESSION.SESSION_KEY_USER);
		if (!raw) {
			return null;
		}
		try {
			return JSON.parse(raw) as UserProfile;
		} catch {
			return null;
		}
	}

	/**
	 * Automatic / idle logout: clears local session and navigates to login
	 * only after POST /auth/logout succeeds. Never strands the user offline.
	 * @returns true when logout completed and local state was cleared
	 */
	async safeSignOut(): Promise<boolean> {
		if (!navigator.onLine) {
			return false;
		}
		try {
			await firstValueFrom(this.httpService.logout());
		} catch {
			return false;
		}
		this.stopIdleWatcher();
		this.clearLocalSession();
		broadcastSessionRevoked();
		assignWindowLocation(loginPathWithSessionUpgrade(RouteType.LOGIN));
		return true;
	}

	/**
	 * Explicit logout (user confirmed). Same safe server-first path.
	 * @returns true when server logout succeeded and local state cleared
	 */
	async signOut(): Promise<boolean> {
		return this.safeSignOut();
	}

	getIDToken(): string | null {
		return null;
	}

	getRoles(): string[] {
		const user = this.userProfile;
		return user?.roles ? [...user.roles] : [];
	}

	getTokenExpiry(): void {
		console.log("BFF mode: token expiry is managed server-side");
	}

	teardownSilentRefresh(): void {
		// No-op in BFF mode.
	}

	restartSilentRefreshTimer(): void {
		// No-op in BFF mode.
	}

	async refreshToken(): Promise<void> {
		if (!navigator.onLine) {
			return;
		}
		try {
			await firstValueFrom(this.httpService.getSession());
		} catch (err) {
			console.warn("BFF session keepalive failed:", err);
		}
	}

	async completePostLoginFlow(): Promise<void> {
		this.loadingService.setLoading(true);
		try {
			const loggedIn = await this.configure();
			if (!loggedIn) {
				this.loginError = "Unable to establish session after login.";
				return;
			}

			const returnUrl = consumeReturnUrl();
			const defaultTarget = `/${this.roleService.getDefaultRoute()}`;
			const normalizedReturnPath = returnUrl?.split(/[?#]/)[0];
			const isDefaultRedirect =
				!normalizedReturnPath ||
				normalizedReturnPath === "/" ||
				normalizedReturnPath === `/${RouteType.DASHBOARD}` ||
				normalizedReturnPath === `/${RouteType.REPORT}` ||
				normalizedReturnPath === `/${RouteType.FORMS}` ||
				normalizedReturnPath === `/${RouteType.RATINGS}`;
			const target = isDefaultRedirect ? defaultTarget : returnUrl!;
			await this.router.navigateByUrl(target);
		} finally {
			this.loadingService.setLoading(false);
		}
	}

	private startIdleWatcher(): void {
		try {
			this.injector.get(OnlineIdleLogoutService).start();
		} catch {
			// ignore if unavailable during tests
		}
	}

	private stopIdleWatcher(): void {
		try {
			this.injector.get(OnlineIdleLogoutService).stop();
		} catch {
			// ignore
		}
	}

	private clearLocalSession(): void {
		localStorage.removeItem(SESSION.SESSION_KEY_OFFLINE_SESSION_EXPIRES_AT);
		localStorage.removeItem(SESSION.SESSION_KEY_USER);
		localStorage.removeItem(SESSION.SESSION_KEY_USER_ROLE);
		localStorage.removeItem("user");
		sessionStorage.removeItem(SESSION.SESSION_KEY_RETURN_URL);
		this.clearProfileCache();
		this.sessionAuthenticated = false;
		this.sessionLoaded = true;
	}

	private clearProfileCache(): void {
		localStorage.removeItem(SESSION.SESSION_KEY_PROFILE);
		try {
			this.injector.get(ProfileService).clearCachedProfile();
		} catch {
			// ignore if unavailable during tests
		}
	}

	private getStoredUserId(): string | undefined {
		return (
			this.parseStoredUserId(localStorage.getItem(SESSION.SESSION_KEY_USER)) ??
			this.parseStoredUserId(localStorage.getItem("user")) ??
			this.roleService.userDetails?.id
		);
	}

	private parseStoredUserId(raw: string | null): string | undefined {
		if (!raw) {
			return undefined;
		}
		try {
			return (JSON.parse(raw) as User).id;
		} catch {
			return undefined;
		}
	}

	private purgeLegacyImplicitTokens(): void {
		for (const key of LEGACY_OAUTH_KEYS) {
			localStorage.removeItem(key);
		}
		void this.clearIndexedDbBearer();
	}

	private async clearIndexedDbBearer(): Promise<void> {
		try {
			const request = indexedDB.open("eTrainingDB");
			request.onsuccess = () => {
				const db = request.result;
				if (!db.objectStoreNames.contains("auth")) {
					db.close();
					return;
				}
				const tx = db.transaction("auth", "readwrite");
				tx.objectStore("auth").delete("bearer");
				tx.oncomplete = () => db.close();
			};
		} catch {
			// ignore
		}
	}

	private hasOfflineProfile(): boolean {
		return !!localStorage.getItem(SESSION.SESSION_KEY_USER);
	}

	private extendOfflineSessionWindow(): void {
		localStorage.setItem(
			SESSION.SESSION_KEY_OFFLINE_SESSION_EXPIRES_AT,
			String(Date.now() + this.offlineSessionDurationMs),
		);
	}

	private async hydrateUserSession(data: User): Promise<void> {
		const previousUserId = this.getStoredUserId();
		if (previousUserId && previousUserId !== data.id) {
			this.clearProfileCache();
		}

		const storedRole = localStorage.getItem(SESSION.SESSION_KEY_USER_ROLE);
		const selectedRole =
			storedRole && data.roles?.includes(storedRole) ? storedRole : undefined;
		const userWithRole = selectedRole ? { ...data, selectedRole } : data;

		localStorage.setItem("user", JSON.stringify(userWithRole));
		this.roleService.userDetails = userWithRole;
		this.extendOfflineSessionWindow();

		if (selectedRole) {
			this.roleService.setRolesBool();
		} else {
			await this.roleService.setInitRole();
		}
		this.roleService.setAuthenticatedUser(userWithRole);

		await this.initService.initMasterData();
		await this.initService.initFormMasterData();

		try {
			const containerRatingsService = this.injector.get(ContainerRatingsService);
			await containerRatingsService.syncPendingDrafts();
		} catch (syncErr) {
			console.warn("Failed to sync pending drafts after login:", syncErr);
		}
	}
}
